import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { StreamFailureRow, StreamFailureStatus } from "@gwrctl/api-client";
import { client } from "../lib/client.js";
import { humanizeError } from "../lib/errors.ts";
import { useToast } from "../lib/toast.tsx";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import { Button, Card, Empty, ErrorState } from "../components/Ui.tsx";

/**
 * Raadiocast'in bildirdiği açılamayan yayın adresleri.
 *
 * Uygulama aynı adresi cihaz ve ağ başına saatte en fazla bir kez bildirir, yani sayılar
 * kabaca "etkilenen kullanıcı". "error" = oynatıcı hata verdi (adres büyük
 * ihtimalle ölü), "timeout" = 12 sn içinde bağlanamadı (ölü ya da çok yavaş).
 * Adres düzeltilince "Düzeltildi" ile kapatılır; yeniden bildirilirse kendiliğinden
 * açılır.
 *
 * Port ve ağ ayrımı kurumsal Wi-Fi sorununu teşhis etmek için: yalnız Wi-Fi'da
 * bildirilen, standart dışı portlu (:8000 gibi) adresler büyük ihtimalle ağ
 * tarafından kesiliyor; o istasyona 443'ten yayın yapan bir kaynak eklemek çözer.
 */
const APP_ID = "raadiocast";

/** Adresin portu; yazılmamışsa şemanın varsayılanı. */
function portOf(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.port || (parsed.protocol === "https:" ? "443" : "80");
  } catch {
    return "?";
  }
}

const STATUS_LABEL: Record<StreamFailureStatus, string> = {
  open: "Açık",
  resolved: "Düzeltildi",
  all: "Tümü",
};

export function StreamFailuresPage(): ReactNode {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState<StreamFailureStatus>("open");
  const [search, setSearch] = useState("");

  const failures = useQuery({
    queryKey: ["stream-failures", status],
    queryFn: () => client.streamFailures(APP_ID, status),
  });

  const resolve = useMutation({
    mutationFn: ({ url, resolved }: { url: string; resolved: boolean }) =>
      client.resolveStreamFailure(APP_ID, url, resolved),
    onSuccess: (_res, { resolved }) => {
      void queryClient.invalidateQueries({ queryKey: ["stream-failures"] });
      toast({ tone: "ok", title: resolved ? "Düzeltildi olarak işaretlendi" : "Yeniden açıldı" });
    },
    // Sessiz kalırsa satır yerinde durur ve işaretlendi sanılır.
    onError: (err, { resolved }) =>
      toast({
        tone: "danger",
        title: resolved ? "İşaretlenemedi" : "Yeniden açılamadı",
        body: humanizeError(err),
      }),
  });

  const needle = search.trim().toLowerCase();
  const rows = (failures.data ?? []).filter(
    (row) =>
      !needle ||
      row.url.toLowerCase().includes(needle) ||
      (row.stationName ?? "").toLowerCase().includes(needle),
  );

  return (
    <>
      <PageHeader
        kicker="raadiocast"
        title="Yayın hataları"
        actions={
          <select
            className="input !w-auto"
            aria-label="Durum"
            value={status}
            onChange={(e) => setStatus(e.target.value as StreamFailureStatus)}
          >
            {(Object.keys(STATUS_LABEL) as StreamFailureStatus[]).map((key) => (
              <option key={key} value={key}>
                {STATUS_LABEL[key]}
              </option>
            ))}
          </select>
        }
      />

      <PageBody>
        <Card
          title={failures.data ? `${rows.length} adres` : "Adresler"}
          hint="cihaz başına saatte bir bildirim · 90 gün sessiz kalan silinir"
          action={
            <input
              className="input w-full sm:!w-56"
              aria-label="İstasyon ya da adres ara"
              placeholder="istasyon ya da adres ara"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          }
          flush
        >
          {failures.isError ? (
            // Hata varken "hata yok" demek yanlış güven verir; ayrı durum.
            <ErrorState error={failures.error} onRetry={() => void failures.refetch()} />
          ) : failures.isLoading ? (
            <Empty>Yükleniyor…</Empty>
          ) : rows.length ? (
            rows.map((row) => (
              <FailureRow
                key={row.url}
                row={row}
                pending={resolve.isPending && resolve.variables?.url === row.url}
                disabled={resolve.isPending}
                onToggle={() => resolve.mutate({ url: row.url, resolved: !row.resolvedAt })}
              />
            ))
          ) : needle ? (
            <Empty>“{search.trim()}” ile eşleşen adres yok</Empty>
          ) : (
            <Empty>
              {status === "open"
                ? "Açık yayın hatası yok"
                : status === "resolved"
                  ? "Düzeltildi olarak işaretlenmiş adres yok"
                  : "Bildirilmiş yayın hatası yok"}
            </Empty>
          )}
        </Card>
      </PageBody>
    </>
  );
}

function FailureRow({
  row,
  pending,
  disabled,
  onToggle,
}: {
  row: StreamFailureRow;
  pending: boolean;
  disabled: boolean;
  onToggle: () => void;
}): ReactNode {
  const total = row.errorCount + row.timeoutCount;
  return (
    <div className="row flex-wrap items-center gap-3">
      <span
        className="mono w-10 shrink-0 text-right text-md"
        style={{ color: row.resolvedAt ? "var(--color-neutral-600)" : "var(--color-danger)" }}
        title={`${row.errorCount} hata · ${row.timeoutCount} zaman aşımı`}
      >
        {total}
      </span>
      <div className="grid min-w-0 flex-1 basis-[220px] gap-1">
        <span className="flex flex-wrap items-center gap-2 text-neutral-100">
          {row.stationName || row.stationId || "adsız istasyon"}
          {row.country && <span className="tag tag-neutral">{row.country}</span>}
          <span className="tag tag-outline">
            {row.errorCount} hata · {row.timeoutCount} zaman aşımı
          </span>
          <span
            className="tag tag-outline"
            style={
              portOf(row.url) === "443"
                ? undefined
                : { color: "var(--color-warn)", borderColor: "var(--color-warn)" }
            }
            title="443 dışındaki portlar kurumsal ağlarda sık kesilir"
          >
            {row.url.startsWith("https:") ? "https" : "http"} :{portOf(row.url)}
          </span>
          {(row.wifiCount > 0 || row.cellularCount > 0) && (
            <span className="tag tag-outline">
              wi-fi {row.wifiCount} · mobil {row.cellularCount}
            </span>
          )}
          {row.hadFallback && <span className="tag tag-accent">başka kaynağı var</span>}
        </span>
        <a
          href={row.url}
          target="_blank"
          rel="noreferrer"
          className="mono truncate text-xs text-accent-300 hover:underline"
        >
          {row.url}
        </a>
        <span className="text-2xs text-neutral-600">
          son {new Date(row.lastSeen).toLocaleString("tr-TR")} · ilk{" "}
          {new Date(row.firstSeen).toLocaleDateString("tr-TR")}
          {row.lastAppVersion ? ` · v${row.lastAppVersion}` : ""}
          {row.resolvedAt
            ? ` · düzeltildi ${new Date(row.resolvedAt).toLocaleDateString("tr-TR")}`
            : ""}
        </span>
      </div>
      <Button variant="ghost" className="!ml-auto" loading={pending} disabled={disabled} onClick={onToggle}>
        {row.resolvedAt ? "Yeniden aç" : "Düzeltildi"}
      </Button>
    </div>
  );
}
